import { authFetch } from '@/lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Plus, Trash2, Edit3, Save, X, ChevronDown, ChevronRight, Star, Calendar, User, Loader2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Score Button ────────────────────────────────────────────────
function ScoreButton({ value, selected, onClick }) {
    const colors = value <= 3 ? 'bg-red-100 text-red-700 border-red-200' :
                   value <= 6 ? 'bg-amber-100 text-amber-700 border-amber-200' :
                                'bg-green-100 text-green-700 border-green-200';
    const activeColors = value <= 3 ? 'bg-red-600 text-white border-red-600' :
                         value <= 6 ? 'bg-amber-500 text-white border-amber-500' :
                                      'bg-green-600 text-white border-green-600';
    return (
        <button
            type="button"
            onClick={() => onClick(value)}
            className={`w-7 h-7 rounded-full text-xs font-bold border transition-all ${selected ? activeColors : colors}`}
            data-testid={`score-btn-${value}`}
        >
            {value}
        </button>
    );
}

// ─── Score Average Badge ─────────────────────────────────────────
function AvgBadge({ score, max, label }) {
    if (max === 0) return null;
    const avg = score / max;
    const color = avg <= 3 ? 'text-red-600 bg-red-50' :
                  avg <= 6 ? 'text-amber-600 bg-amber-50' :
                             'text-green-600 bg-green-50';
    return (
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${color}`} data-testid={`avg-${label}`}>
            {avg.toFixed(1)}/10
        </span>
    );
}

// ─── Evaluation Form ─────────────────────────────────────────────
function EvaluationForm({ template, existingEval, onSave, onCancel }) {
    const [date, setDate] = useState(existingEval?.date || new Date().toISOString().split('T')[0]);
    const [evaluatorName, setEvaluatorName] = useState(existingEval?.evaluator_name || '');
    const [evaluatedName, setEvaluatedName] = useState(existingEval?.evaluated_name || '');
    const [evalType, setEvalType] = useState(existingEval?.eval_type || 'self');
    const [scores, setScores] = useState(existingEval?.scores || {});
    const [expanded, setExpanded] = useState({});
    const [saving, setSaving] = useState(false);

    // Expand all rubrics by default
    useEffect(() => {
        const exp = {};
        (template?.rubrics || []).forEach(r => {
            exp[r.id] = true;
            (r.sub_rubrics || []).forEach(sr => { exp[sr.id] = true; });
        });
        setExpanded(exp);
    }, [template]);

    const setScore = (elemId, value) => {
        setScores(prev => ({ ...prev, [elemId]: prev[elemId] === value ? undefined : value }));
    };

    const getSubRubricScore = (sr) => {
        const elems = sr.elements || [];
        if (elems.length === 0) {
            // Sub-rubrique sans éléments: note directe
            return { score: scores[sr.id] ?? null, max: 1, count: scores[sr.id] != null ? 1 : 0 };
        }
        let total = 0, count = 0;
        elems.forEach(e => { if (scores[e.id] != null) { total += scores[e.id]; count++; } });
        return { score: total, max: elems.length, count };
    };

    const getRubricScore = (rubric) => {
        let totalScore = 0, totalMax = 0, totalCount = 0;
        (rubric.sub_rubrics || []).forEach(sr => {
            const s = getSubRubricScore(sr);
            totalScore += s.score || 0;
            if (sr.elements?.length > 0) {
                totalMax += sr.elements.length;
                totalCount += s.count;
            } else {
                totalMax += 1;
                totalCount += s.count;
            }
        });
        return { score: totalScore, max: totalMax, count: totalCount };
    };

    const getGeneralScore = () => {
        let totalScore = 0, totalMax = 0;
        (template?.rubrics || []).forEach(r => {
            const s = getRubricScore(r);
            totalScore += s.score;
            totalMax += s.max;
        });
        return { score: totalScore, max: totalMax };
    };

    const handleSave = async () => {
        if (!evaluatedName.trim()) { toast.error("Nom de l'évalué requis"); return; }
        setSaving(true);
        try {
            await onSave({
                date,
                evaluator_name: evaluatorName,
                evaluated_name: evaluatedName,
                eval_type: evalType,
                scores,
                template_snapshot: template.rubrics,
            });
        } finally { setSaving(false); }
    };

    const general = getGeneralScore();

    return (
        <div className="space-y-4" data-testid="evaluation-form">
            {/* Header info */}
            <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2.5">
                <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider">Date</label>
                    <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-9 text-sm mt-0.5" data-testid="eval-date" />
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setEvalType('self')}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${evalType === 'self' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}
                        data-testid="eval-type-self"
                    >Auto-evaluation</button>
                    <button
                        type="button"
                        onClick={() => setEvalType('manager')}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${evalType === 'manager' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}
                        data-testid="eval-type-manager"
                    >Par un responsable</button>
                </div>
                <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider">Personne evaluee</label>
                    <Input value={evaluatedName} onChange={e => setEvaluatedName(e.target.value)} placeholder="Nom..." className="h-9 text-sm mt-0.5" data-testid="eval-evaluated" />
                </div>
                {evalType === 'manager' && (
                    <div>
                        <label className="text-[10px] text-slate-500 uppercase tracking-wider">Evaluateur</label>
                        <Input value={evaluatorName} onChange={e => setEvaluatorName(e.target.value)} placeholder="Nom du responsable..." className="h-9 text-sm mt-0.5" data-testid="eval-evaluator" />
                    </div>
                )}
            </div>

            {/* General Score */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-xl p-3 flex items-center justify-between">
                <span className="text-sm font-semibold">Score General</span>
                <span className="text-xl font-bold" data-testid="general-score">
                    {general.max > 0 ? (general.score / general.max).toFixed(1) : '-'}/10
                </span>
            </div>

            {/* Rubrics */}
            {(template?.rubrics || []).sort((a, b) => a.order - b.order).map(rubric => {
                const rubricScore = getRubricScore(rubric);
                return (
                    <div key={rubric.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`rubric-${rubric.id}`}>
                        {/* Rubric header */}
                        <button
                            onClick={() => setExpanded(p => ({ ...p, [rubric.id]: !p[rubric.id] }))}
                            className="w-full flex items-center gap-2 px-3 py-2.5 bg-slate-100 border-b border-slate-200"
                        >
                            {expanded[rubric.id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                            <span className="flex-1 text-left text-sm font-bold text-slate-800 uppercase tracking-wide">{rubric.name}</span>
                            <AvgBadge score={rubricScore.score} max={rubricScore.max} label={rubric.name} />
                        </button>

                        {expanded[rubric.id] && (
                            <div className="divide-y divide-slate-100">
                                {(rubric.sub_rubrics || []).sort((a, b) => a.order - b.order).map(sr => {
                                    const srScore = getSubRubricScore(sr);
                                    const hasElements = sr.elements && sr.elements.length > 0;
                                    return (
                                        <div key={sr.id} data-testid={`subrubric-${sr.id}`}>
                                            {/* Sub-rubric header */}
                                            <div
                                                className="flex items-center gap-2 px-3 py-2 bg-slate-50/50 cursor-pointer"
                                                onClick={() => hasElements && setExpanded(p => ({ ...p, [sr.id]: !p[sr.id] }))}
                                            >
                                                {hasElements ? (
                                                    expanded[sr.id] ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                                                ) : <div className="w-3.5" />}
                                                <span className="flex-1 text-xs font-semibold text-slate-600">{sr.name}</span>
                                                {hasElements ? (
                                                    <AvgBadge score={srScore.score} max={srScore.max} label={sr.name} />
                                                ) : (
                                                    /* Direct score for sub-rubrique without elements */
                                                    <div className="flex gap-0.5">
                                                        {[...Array(11)].map((_, v) => (
                                                            <ScoreButton key={v} value={v} selected={scores[sr.id] === v} onClick={(val) => setScore(sr.id, val)} />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Elements */}
                                            {hasElements && expanded[sr.id] && (
                                                <div className="px-3 pb-1">
                                                    {sr.elements.sort((a, b) => a.order - b.order).map(elem => (
                                                        <div key={elem.id} className="flex items-center gap-2 py-1.5" data-testid={`element-${elem.id}`}>
                                                            <span className="flex-1 text-xs text-slate-700 pl-5">{elem.name}</span>
                                                            <div className="flex gap-0.5">
                                                                {[...Array(11)].map((_, v) => (
                                                                    <ScoreButton key={v} value={v} selected={scores[elem.id] === v} onClick={(val) => setScore(elem.id, val)} />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Actions */}
            <div className="flex gap-2 pb-4">
                <Button variant="outline" className="flex-1" onClick={onCancel}>
                    <X className="w-4 h-4 mr-1" /> Annuler
                </Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleSave} disabled={saving} data-testid="eval-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Enregistrer
                </Button>
            </div>
        </div>
    );
}

// ─── Evaluation View (read-only) ─────────────────────────────────
function EvaluationView({ evaluation, onBack }) {
    const rubrics = evaluation.template_snapshot || [];
    const scores = evaluation.scores || {};

    const getSubRubricScore = (sr) => {
        const elems = sr.elements || [];
        if (elems.length === 0) return { score: scores[sr.id] ?? 0, max: 1 };
        let total = 0; elems.forEach(e => { total += (scores[e.id] || 0); });
        return { score: total, max: elems.length };
    };

    const getRubricScore = (rubric) => {
        let s = 0, m = 0;
        (rubric.sub_rubrics || []).forEach(sr => {
            const sub = getSubRubricScore(sr);
            s += sub.score; m += sub.max;
        });
        return { score: s, max: m };
    };

    const general = (() => {
        let s = 0, m = 0;
        rubrics.forEach(r => { const rs = getRubricScore(r); s += rs.score; m += rs.max; });
        return { score: s, max: m };
    })();

    return (
        <div className="space-y-3" data-testid="evaluation-view">
            <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 mb-2 active:text-slate-700">
                <ArrowLeft className="w-4 h-4" /> Retour
            </button>

            {/* Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-1">
                <div className="flex items-center justify-between">
                    <h2 className="font-bold text-slate-900">{evaluation.evaluated_name}</h2>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${evaluation.eval_type === 'self' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {evaluation.eval_type === 'self' ? 'Auto-evaluation' : 'Manager'}
                    </span>
                </div>
                <p className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{evaluation.date}</p>
                {evaluation.evaluator_name && (
                    <p className="text-xs text-slate-500 flex items-center gap-1"><User className="w-3.5 h-3.5" />Par : {evaluation.evaluator_name}</p>
                )}
            </div>

            {/* General */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-xl p-3 flex items-center justify-between">
                <span className="text-sm font-semibold">Score General</span>
                <span className="text-xl font-bold">{general.max > 0 ? (general.score / general.max).toFixed(1) : '-'}/10</span>
            </div>

            {/* Rubrics summary */}
            {rubrics.sort((a, b) => a.order - b.order).map(rubric => {
                const rs = getRubricScore(rubric);
                return (
                    <div key={rubric.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2.5 bg-slate-100 border-b border-slate-200">
                            <span className="text-sm font-bold text-slate-800 uppercase tracking-wide">{rubric.name}</span>
                            <AvgBadge score={rs.score} max={rs.max} label={rubric.name} />
                        </div>
                        <div className="divide-y divide-slate-100">
                            {(rubric.sub_rubrics || []).sort((a, b) => a.order - b.order).map(sr => {
                                const srs = getSubRubricScore(sr);
                                const hasElements = sr.elements && sr.elements.length > 0;
                                return (
                                    <div key={sr.id}>
                                        <div className="flex items-center justify-between px-3 py-2 bg-slate-50/50">
                                            <span className="text-xs font-semibold text-slate-600">{sr.name}</span>
                                            <AvgBadge score={srs.score} max={srs.max} label={sr.name} />
                                        </div>
                                        {hasElements && (
                                            <div className="px-3 pb-1">
                                                {sr.elements.sort((a, b) => a.order - b.order).map(elem => (
                                                    <div key={elem.id} className="flex items-center justify-between py-1">
                                                        <span className="text-xs text-slate-600 pl-5">{elem.name}</span>
                                                        <span className={`text-xs font-bold ${(scores[elem.id] ?? 0) <= 3 ? 'text-red-600' : (scores[elem.id] ?? 0) <= 6 ? 'text-amber-600' : 'text-green-600'}`}>
                                                            {scores[elem.id] != null ? `${scores[elem.id]}/10` : '-'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {!hasElements && (
                                            <div className="px-3 py-1">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-slate-500 pl-5">Note</span>
                                                    <span className={`text-xs font-bold ${(scores[sr.id] ?? 0) <= 3 ? 'text-red-600' : (scores[sr.id] ?? 0) <= 6 ? 'text-amber-600' : 'text-green-600'}`}>
                                                        {scores[sr.id] != null ? `${scores[sr.id]}/10` : '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Admin Template Editor ───────────────────────────────────────
function TemplateEditor({ template, onSave, onCancel }) {
    const [rubrics, setRubrics] = useState(JSON.parse(JSON.stringify(template?.rubrics || [])));
    const [saving, setSaving] = useState(false);

    const addRubric = () => {
        setRubrics(prev => [...prev, { id: crypto.randomUUID(), name: '', order: prev.length, sub_rubrics: [] }]);
    };
    const addSubRubric = (rubricIdx) => {
        setRubrics(prev => {
            const copy = JSON.parse(JSON.stringify(prev));
            copy[rubricIdx].sub_rubrics.push({ id: crypto.randomUUID(), name: '', order: copy[rubricIdx].sub_rubrics.length, elements: [] });
            return copy;
        });
    };
    const addElement = (rubricIdx, srIdx) => {
        setRubrics(prev => {
            const copy = JSON.parse(JSON.stringify(prev));
            copy[rubricIdx].sub_rubrics[srIdx].elements.push({ id: crypto.randomUUID(), name: '', order: copy[rubricIdx].sub_rubrics[srIdx].elements.length });
            return copy;
        });
    };
    const removeItem = (rubricIdx, srIdx, elemIdx) => {
        setRubrics(prev => {
            const copy = JSON.parse(JSON.stringify(prev));
            if (elemIdx != null) {
                copy[rubricIdx].sub_rubrics[srIdx].elements.splice(elemIdx, 1);
            } else if (srIdx != null) {
                copy[rubricIdx].sub_rubrics.splice(srIdx, 1);
            } else {
                copy.splice(rubricIdx, 1);
            }
            return copy;
        });
    };
    const updateName = (rubricIdx, srIdx, elemIdx, value) => {
        setRubrics(prev => {
            const copy = JSON.parse(JSON.stringify(prev));
            if (elemIdx != null) copy[rubricIdx].sub_rubrics[srIdx].elements[elemIdx].name = value;
            else if (srIdx != null) copy[rubricIdx].sub_rubrics[srIdx].name = value;
            else copy[rubricIdx].name = value;
            return copy;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try { await onSave(rubrics); } finally { setSaving(false); }
    };

    return (
        <div className="space-y-3" data-testid="template-editor">
            <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-bold text-slate-900">Gerer les rubriques</h2>
                <Button size="sm" variant="outline" onClick={addRubric}><Plus className="w-3.5 h-3.5 mr-1" />Rubrique</Button>
            </div>

            {rubrics.map((rubric, ri) => (
                <div key={rubric.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 border-b border-slate-200">
                        <Input value={rubric.name} onChange={e => updateName(ri, null, null, e.target.value)} placeholder="Rubrique..." className="h-8 text-sm font-bold flex-1" />
                        <button onClick={() => removeItem(ri)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="p-2 space-y-2">
                        {rubric.sub_rubrics.map((sr, si) => (
                            <div key={sr.id} className="bg-slate-50 rounded-lg p-2">
                                <div className="flex items-center gap-2 mb-1">
                                    <Input value={sr.name} onChange={e => updateName(ri, si, null, e.target.value)} placeholder="Sous-rubrique..." className="h-7 text-xs flex-1" />
                                    <button onClick={() => addElement(ri, si)} className="text-blue-400 hover:text-blue-600"><Plus className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => removeItem(ri, si)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                                {sr.elements.map((elem, ei) => (
                                    <div key={elem.id} className="flex items-center gap-2 ml-4 mb-1">
                                        <Input value={elem.name} onChange={e => updateName(ri, si, ei, e.target.value)} placeholder="Element..." className="h-6 text-[11px] flex-1" />
                                        <button onClick={() => removeItem(ri, si, ei)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                ))}
                            </div>
                        ))}
                        <Button size="sm" variant="ghost" className="text-xs text-slate-500 w-full" onClick={() => addSubRubric(ri)}>
                            <Plus className="w-3 h-3 mr-1" />Sous-rubrique
                        </Button>
                    </div>
                </div>
            ))}

            <div className="flex gap-2 pb-4">
                <Button variant="outline" className="flex-1" onClick={onCancel}><X className="w-4 h-4 mr-1" />Annuler</Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleSave} disabled={saving} data-testid="template-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Sauvegarder
                </Button>
            </div>
        </div>
    );
}

// ─── Main Page ───────────────────────────────────────────────────
export default function GuideEvaluationsPage() {
    const navigate = useNavigate();
    const isAdmin = JSON.parse(localStorage.getItem('guide_user') || '{}').role === 'admin';
    const [template, setTemplate] = useState(null);
    const [evaluations, setEvaluations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list'); // list | new | edit | view
    const [currentEval, setCurrentEval] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            const [tplRes, evalsRes] = await Promise.all([
                authFetch(`${API}/api/guide/evaluation-template`).then(r => r.json()),
                authFetch(`${API}/api/guide/evaluations`).then(r => r.json()),
            ]);
            setTemplate(tplRes);
            setEvaluations(evalsRes);
        } catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSaveEvaluation = async (data) => {
        try {
            const res = await authFetch(`${API}/api/guide/evaluations`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
            });
            if (!res.ok) throw new Error();
            toast.success('Evaluation enregistree');
            setView('list');
            fetchData();
        } catch { toast.error("Erreur lors de l'enregistrement"); }
    };

    const handleDeleteEvaluation = async (id) => {
        if (!window.confirm('Supprimer cette evaluation ?')) return;
        try {
            await authFetch(`${API}/api/guide/evaluations/${id}`, { method: 'DELETE' });
            toast.success('Evaluation supprimee');
            fetchData();
        } catch { toast.error('Erreur'); }
    };

    const handleSaveTemplate = async (rubrics) => {
        try {
            const res = await authFetch(`${API}/api/guide/evaluation-template`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rubrics }),
            });
            if (!res.ok) throw new Error();
            const updated = await res.json();
            setTemplate(updated);
            toast.success('Rubriques mises a jour');
            setView('list');
        } catch { toast.error('Erreur'); }
    };

    const viewEvaluation = async (id) => {
        try {
            const res = await authFetch(`${API}/api/guide/evaluations/${id}`);
            const data = await res.json();
            setCurrentEval(data);
            setView('view');
        } catch { toast.error('Erreur'); }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        );
    }

    // Template editor
    if (view === 'edit') {
        return (
            <div className="px-4 py-5">
                <TemplateEditor template={template} onSave={handleSaveTemplate} onCancel={() => setView('list')} />
            </div>
        );
    }

    // New evaluation form
    if (view === 'new') {
        return (
            <div className="px-4 py-5">
                <h2 className="text-base font-bold text-slate-900 mb-3">Nouvelle evaluation</h2>
                <EvaluationForm template={template} onSave={handleSaveEvaluation} onCancel={() => setView('list')} />
            </div>
        );
    }

    // View evaluation
    if (view === 'view' && currentEval) {
        return (
            <div className="px-4 py-5">
                <EvaluationView evaluation={currentEval} onBack={() => { setView('list'); setCurrentEval(null); }} />
            </div>
        );
    }

    // List view
    return (
        <div className="px-4 py-5" data-testid="guide-evaluations">
            <button onClick={() => navigate('/guide')} className="flex items-center gap-1 text-sm text-slate-500 mb-4 active:text-slate-700">
                <ArrowLeft className="w-4 h-4" /> Accueil
            </button>

            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold text-slate-900">Evaluations</h1>
                {isAdmin && <Button size="sm" variant="ghost" className="text-slate-500 text-xs" onClick={() => setView('edit')} data-testid="edit-template-btn">
                    <Edit3 className="w-3.5 h-3.5 mr-1" />Rubriques
                </Button>}
            </div>

            <Button className="w-full bg-red-600 hover:bg-red-700 text-white mb-4" onClick={() => setView('new')} data-testid="new-eval-btn">
                <Plus className="w-4 h-4 mr-2" /> Nouvelle evaluation
            </Button>

            {/* History */}
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Historique</h2>
            {evaluations.length === 0 ? (
                <div className="text-center py-8">
                    <Star className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-400">Aucune evaluation</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {evaluations.map(ev => {
                        const rubrics = ev.template_snapshot || [];
                        const scores = ev.scores || {};
                        let totalScore = 0, totalMax = 0;
                        rubrics.forEach(r => {
                            (r.sub_rubrics || []).forEach(sr => {
                                if (sr.elements?.length > 0) {
                                    sr.elements.forEach(e => { totalScore += (scores[e.id] || 0); });
                                    totalMax += sr.elements.length;
                                } else {
                                    totalScore += (scores[sr.id] || 0);
                                    totalMax += 1;
                                }
                            });
                        });
                        const avg = totalMax > 0 ? (totalScore / totalMax).toFixed(1) : '-';

                        return (
                            <div key={ev.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex items-center gap-3" data-testid={`eval-item-${ev.id}`}>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-900">{ev.evaluated_name}</p>
                                    <p className="text-[11px] text-slate-400 flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />{ev.date}
                                        <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${ev.eval_type === 'self' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                                            {ev.eval_type === 'self' ? 'Auto' : 'Manager'}
                                        </span>
                                    </p>
                                </div>
                                <span className={`text-sm font-bold ${parseFloat(avg) <= 3 ? 'text-red-600' : parseFloat(avg) <= 6 ? 'text-amber-600' : 'text-green-600'}`}>
                                    {avg}/10
                                </span>
                                <button onClick={() => viewEvaluation(ev.id)} className="text-slate-400 hover:text-slate-600" data-testid={`view-eval-${ev.id}`}>
                                    <Eye className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteEvaluation(ev.id)} className="text-slate-400 hover:text-red-600" data-testid={`delete-eval-${ev.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
